import { useCallback, useSyncExternalStore } from 'react';
import { trustedTime } from '~/shared/utils/trusted-time';

// `setTimeout` fires right away if asked to wait more than ~24 days.
// This is a known limitation, so we will split the timer into 7-day batches.
const TIMER_SLICE_MS = 7 * 24 * 60 * 60 * 1000;

// How long the countdown stays up after launch showing the offer is "live".
const PRELAUNCH_EXPIRED_TEXT_PERSIST_MS = 24 * 60 * 60 * 1000;

const getPrelaunchEndDate = (prelaunch?: string | null): Date | null => {
    if (!prelaunch) return null;

    const endDate = new Date(prelaunch);

    return Number.isNaN(endDate.getTime()) ? null : endDate;
};

export type PrelaunchGate = {
    /** The offer is announced but not yet buyable. */
    isBlockingBuy: boolean;
    /** Flags the countdown's visibility. Stays true for a day after launch, saying "live". */
    isCountdownVisible: boolean;
};

/** Returns `true` until the clock reaches `timestamp`. */
const useIsBefore = (timestamp: number | null, serverValue: boolean): boolean => {
    // This tells us when to do another `readIsBefore` call.
    const subscribe = useCallback(
        (onChange: () => void) => {
            if (timestamp === null) return () => undefined;

            let timer: ReturnType<typeof setTimeout> | undefined;

            const startTimer = () => {
                const timeRemaining = timestamp - trustedTime.now();

                if (timeRemaining <= 0) return;

                // Split into TIMER_SLICE_MS chunks, so start another timer after each slice.
                timer = setTimeout(
                    () => {
                        onChange();
                        startTimer();
                    },
                    Math.min(timeRemaining, TIMER_SLICE_MS),
                );
            };

            // A reset clock leaves our timer aimed at the wrong moment. Start it over.
            const restartTimer = () => {
                clearTimeout(timer);
                startTimer();
                onChange();
            };

            startTimer();

            const unsubscribe = trustedTime.subscribe(restartTimer);

            return () => {
                clearTimeout(timer);
                unsubscribe();
            };
        },
        [timestamp],
    );

    // This is our "getter"-- its return is what the resulting value is of `useSyncExternalStore`.
    // It is also called every component render.
    const readIsBefore = useCallback(
        () => timestamp !== null && trustedTime.now() < timestamp,
        [timestamp],
    );

    // This is our initial value (server-side). Only read on the first render, so it does not
    // need to react to later changes.
    const readIsBeforeOnServer = useCallback(() => serverValue, []);

    return useSyncExternalStore(subscribe, readIsBefore, readIsBeforeOnServer);
};

/**
 * Works out both prelaunch states from the launch date.
 *
 * Kept here instead of in the countdown component, so the buy button never depends on a
 * countdown being on screen.
 */
export const usePrelaunchGate = (prelaunch?: string | null): PrelaunchGate => {
    const endsAt = getPrelaunchEndDate(prelaunch)?.getTime() ?? null;
    const countdownEndsAt = endsAt === null ? null : endsAt + PRELAUNCH_EXPIRED_TEXT_PERSIST_MS;

    return {
        /*
         * Product actions are blocked until `endsAt`. On the server this is `false` - the page
         * is cached and shared, so any answer it gave would be stale for whoever loads it later.
         */
        isBlockingBuy: useIsBefore(endsAt, false),

        /*
         * Countdown is visible until `countdownEndsAt` (`endsAt` plus the persist window).
         * Safe to answer on the server: the window is a day wide, so a page cached for minutes
         * is nowhere near its edge.
         */
        isCountdownVisible: useIsBefore(
            countdownEndsAt,
            countdownEndsAt !== null && trustedTime.now() < countdownEndsAt,
        ),
    };
};
